<template>
  <div
    class="rs-off-canvas"
    :class="{ 'rs-off-canvas--open': input }"
  >
    <div class="rs-off-canvas__inner">
      <div class="rs-off-canvas__wrap">
        <nav
          v-if="showNav"
          class="rs-off-canvas__nav"
        >
          <div>
            <RsButton
              v-for="(l, idx) in links"
              :key="idx"
              :icon="l.icon"
              variant="white"
              weight="far"
              class="mr-2"
              hollow
              @click="emitTo({ ...l }, {
                emitToComponent: l.emitToComponent,
                eventName: l.eventName,
              })"
            >
              {{ l.label }}
            </RsButton>

            <RsButton
              hollow
              variant="white"
              icon="chevron-left"
              weight="far"
              @click.native="navLeft"
            />
            <RsButton
              hollow
              variant="white"
              icon="chevron-right"
              weight="far"
              class="ml-2"
              @click.native="navRight"
            />
          </div>
          <RsButton
            icon="times"
            plain
            class="oc-close ml-auto"
            @click.native="updateOpenState"
          />
        </nav>
        <div
          v-if="$slots.header && !centerHeader"
          class="rs-off-canvas__header"
        >
          <slot name="header" />
        </div>
        <div
          v-if="$slots.header && centerHeader"
          class="d-flex flex-column align-items-center p-0 mt-4"
        >
          <slot name="header" />
        </div>
        <div
          v-if="$slots.default"
          class="rs-off-canvas__body"
        >
          <slot />
        </div>
        <footer
          v-if="isUpdatePermitted"
          class="rs-off-canvas__footer"
        >
          <div
            class="mr-auto"
          >
            <RsButton
              v-if="discard"
              variant="white"
              plain
              @click.native="updateOpenState"
            >
              Discard Changes
            </RsButton>
          </div>
          <div class="d-flex">
            <div>
              <RsButton
                v-if="showCancel"
                variant="cancel"
                @click="toggleNav"
              >
                Cancel
              </RsButton>
            </div>
            <div>
              <RsButton
                :disabled="disabled"
                variant="primary"
                @click="handleSave"
              >
                Save
              </RsButton>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IPropsClickConfig } from '@/shared';

export default defineComponent({
  name: 'RsOffCanvas',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    discard: {
      type: Boolean,
      default: true,
    },
    showNav: {
      type: Boolean,
      default: true,
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
    centerHeader: {
      type: Boolean,
      default: false,
    },
    links: {
      type: Array as () => IPropsClickConfig[],
      default: () => [],
    },
  },

  data() {
    return {
      input: !!this.value,
    };
  },

  watch: {
    value() {
      this.input = !!this.value;
    },
  },

  methods: {
    toggleNav() {
      this.updateOpenState();
    },
    openNav() {
      this.input = true;
      this.updateOpenState();
    },
    closeNav() {
      this.input = false;
      this.updateOpenState();
    },
    navLeft() {
      this.$emit('navLeft');
    },
    navRight() {
      this.$emit('navRight');
    },
    updateOpenState() {
      this.$emit('input', !this.input);
    },
    handleSave() {
      this.$emit('save');
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
$sp: 2.5rem; //Side padding
.rs-off-canvas{
  position: fixed;
  top: 0;
  right: -$oc-width;
  bottom: 0;
  width: $oc-width;
  border-radius: 20px 0 0 20px;
  background-color: $secondary-color-dark;
  box-shadow: -1px 0px 17px 7px rgba(0,0,0,0.08);
  z-index: 9999;
  transition: transform 0.3s ease;

  &--open{
    transform: translateX(-$oc-width);
  }
  &--closed{
    transform: translateX(0);
  }
  &__inner{
    position: relative;
    flex-direction: column;
    height: 100%;
    display: flex;
  }
  &__nav{
    padding: 2rem 2rem 1.5rem 2rem;
    width: $oc-width;
    display: flex;
    .rs-button{
      opacity: 0.25;
      &:hover{
        opacity: 1;
      }
    }
    .oc-close{
      font-size: 26px;
      color: $white;
      opacity: 0.25;
      &:hover{
        opacity: 1;
      }
    }
  }
  &__wrap{
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    width: $oc-width;
    height: 100%;
    border-radius: 20px 0 0 20px;
  }
  &__header{
    display: flex;
    align-items: center;
    padding: 0 $sp 1rem $sp;
    .rs-avatar{
      width: 85px;
      height: 85px;
      margin-right: 1rem;
    }
    .title{
      color: $primary-color;
      margin-bottom: 0.25rem;
      font-size: rem-calc(24px);
      font-weight: 700;
      .rs-chip{
        margin-left: 1rem;
      }
    }
  }
  &__body{
    flex-grow: 1;
    overflow: auto;
    h4{
      margin-bottom: 2rem;
      color: $primary-color;
      font-weight: 700;
    }
    .tab-nav{
      position: fixed;
      margin-bottom: 1rem;
      width: 100%;
      z-index: 2;
      background-color: $secondary-color-dark;
      border-bottom: 1px solid rgba($white, 0.15);

      &__item{
        margin: 0 1.5rem;
        padding-bottom: 1rem;
        &#nav-tab-1{
          margin-left: 2.5rem;
        }
        span{
          color: $white;
          opacity: 0.4;
        }
        &--active{
          span{
            opacity: 1;
          }
        }
      }
    }
    .tab-panel{
      padding: 4rem $sp 2rem $sp;
      color: $white;
    }
  }
  &__footer{
    position: relative;
    z-index: 10;
    margin-top: auto;
    padding: 1rem $sp;
    width: $oc-width;
    height: 75px;
    display: flex;
    border-top: 1px solid rgba($white, 0.15);
    .rs-button{
      margin-left: 1rem;
      &--plain{
        color: $white;
        opacity: 0.4;
        font-size: 0.875rem;
        &:hover{
          color: $white;
          opacity: 1;
        }
      }
    }
  }

  &.off-canvas-dark {
    background-color: $secondary-color-dark;
    color: $white;
    .oc-header {
      .title {
        color: $primary-color;
      }
      .subtitle {
        color: $white;
      }
    }
    .rs-off-canvas__footer {
      background-color: $secondary-color-dark;
      border-top: none;
      justify-content: space-between;
    }
    & ::v-deep .rs-tabs.tabs {
      padding: 15px;
      .nav-link.active {
        color: $white;
      }
    }
    .rs-tabs .nav-tabs .nav-item a {
      color: $extra-light-gray;
    }
  }

  &.off-canvas-light {
    background-color: $white;
    border-radius: 4px;
    .oc-header {
      .title {
        color: $primary-color;
      }
      .subtitle {
        color: $white;
      }
    }
    .rs-off-canvas__footer {
      background-color: $white;
      border-top: 1px solid $extra-light-gray;
      justify-content: space-between;
    }
    & ::v-deep .rs-tabs.tabs {
      padding: 15px;
      .nav-link.active {
        color: $white;
      }
    }
    .rs-tabs .nav-tabs .nav-item a {
      color: $extra-light-gray;
    }
  }

}
</style>
